import React, { useState } from "react";
import {
  Maximize2,
  Crosshair,
  Gauge,
  Sun,
  Layers,
  Ruler,
  Check,
  ArrowRight,
  ExternalLink,
} from "lucide-react";
import { printersData } from "../data/mock";

const badgeStyles = {
  orange: "bg-[#F76B1C]/15 text-[#F76B1C] border-[#F76B1C]/25",
  teal: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25",
  white: "bg-white/10 text-white/80 border-white/15",
};

const specIcons = {
  buildVolume: Maximize2,
  resolution: Crosshair,
  speed: Gauge,
  lightSource: Sun,
  materials: Layers,
  layerThickness: Ruler,
};

const comparisonSpecs = [
  { key: "buildVolume", label: "Build Volume", icon: Maximize2 },
  { key: "resolution", label: "XY Resolution", icon: Crosshair },
  { key: "speed", label: "Print Speed", icon: Gauge },
  { key: "lightSource", label: "Light Source", icon: Sun },
  { key: "layerThickness", label: "Layer Height", icon: Ruler },
  { key: "materials", label: "Materials", icon: Layers },
  { key: "technology", label: "Technology", icon: Crosshair },
];

export const PrintersSection = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [compareA, setCompareA] = useState("form-4");
  const [compareB, setCompareB] = useState("form-4l");

  const printerA = printersData.find((p) => p.id === compareA);
  const printerB = printersData.find((p) => p.id === compareB);

  return (
    <section id="printers" className="py-24 lg:py-32 bg-[#0A0A0A] relative">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-0 w-[600px] h-[600px] bg-[#F76B1C]/[0.015] rounded-full blur-[160px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#F76B1C]/[0.01] rounded-full blur-[140px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <span className="text-[#F76B1C] text-sm font-semibold uppercase tracking-[0.15em]">
            The Printers
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
            Four Printers. One Ecosystem.
          </h2>
          <p className="mt-6 text-lg text-white/45 leading-relaxed">
            From desktop prototyping to large-format medical manufacturing,
            there&apos;s a Form 4 for every workflow. All share the same MSLA
            engine, software, and material ecosystem.
          </p>
        </div>

        {/* Printer cards grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {printersData.map((printer, i) => (
            <div
              key={printer.id}
              className="group relative rounded-2xl bg-[#111111] border border-white/[0.06] hover:border-[#F76B1C]/25 transition-all duration-500 overflow-hidden hover:-translate-y-1"
              onMouseEnter={() => setHoveredCard(printer.id)}
              onMouseLeave={() => setHoveredCard(null)}
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              {/* Top section: Image + badge */}
              <div className="relative p-6 pb-0 lg:p-8 lg:pb-0">
                {/* Badge */}
                <div className="flex items-center justify-between mb-4">
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${
                      badgeStyles[printer.badgeStyle]
                    }`}
                  >
                    {printer.badge}
                  </span>
                  <span className="text-white/30 text-xs font-medium">
                    {printer.technology}
                  </span>
                </div>

                {/* Printer image */}
                <div className="relative flex items-center justify-center py-6 lg:py-8">
                  <div
                    className={`absolute w-[200px] h-[200px] lg:w-[260px] lg:h-[260px] rounded-full blur-[80px] transition-opacity duration-500 ${
                      hoveredCard === printer.id
                        ? "bg-[#F76B1C]/[0.08] opacity-100"
                        : "bg-[#F76B1C]/[0.03] opacity-60"
                    }`}
                  />
                  <img loading="lazy"
                    src={printer.image}
                    alt={printer.name}
                    className="relative z-10 h-[200px] lg:h-[240px] w-auto object-contain group-hover:scale-105 transition-transform duration-500 drop-shadow-[0_10px_40px_rgba(247,107,28,0.08)]"
                  />
                </div>
              </div>

              {/* Content section */}
              <div className="p-6 lg:p-8 pt-2 lg:pt-3">
                {/* Name + tagline */}
                <h3 className="text-2xl lg:text-3xl font-bold text-white mb-1.5">
                  {printer.name}
                </h3>
                <p className="text-[#F76B1C] text-sm font-medium mb-4">
                  {printer.tagline}
                </p>

                {/* Specs grid */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {[
                    {
                      key: "buildVolume",
                      label: "Build Volume",
                      value: printer.buildVolume,
                    },
                    {
                      key: "resolution",
                      label: "XY Resolution",
                      value: printer.resolution,
                    },
                    {
                      key: "speed",
                      label: "Print Speed",
                      value: printer.speed,
                    },
                    {
                      key: "lightSource",
                      label: "Light Source",
                      value: printer.lightSource,
                    },
                    {
                      key: "layerThickness",
                      label: "Layer Height",
                      value: printer.layerThickness,
                    },
                    {
                      key: "materials",
                      label: "Materials",
                      value: printer.materials,
                    },
                  ].map((spec) => {
                    const IconComp = specIcons[spec.key];
                    return (
                      <div
                        key={spec.key}
                        className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.04] group-hover:border-white/[0.06] transition-colors duration-300"
                      >
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <IconComp
                            size={12}
                            className="text-[#F76B1C]/60"
                          />
                          <span className="text-[10px] text-white/30 font-medium uppercase tracking-wider">
                            {spec.label}
                          </span>
                        </div>
                        <span className="text-xs lg:text-sm text-white/80 font-semibold leading-tight block">
                          {spec.value}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Ideal for */}
                <div className="mb-6">
                  <span className="text-xs text-white/30 font-semibold uppercase tracking-wider mb-2.5 block">
                    Ideal For
                  </span>
                  <div className="space-y-1.5">
                    {printer.idealFor.map((item, j) => (
                      <div
                        key={j}
                        className="flex items-start gap-2 text-sm text-white/50"
                      >
                        <Check
                          size={14}
                          className="text-[#F76B1C]/60 mt-0.5 shrink-0"
                        />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Price + CTAs */}
                <div className="flex items-center justify-between pt-5 border-t border-white/[0.06]">
                  <div>
                    <span className="text-xl lg:text-2xl font-bold text-white">
                      {printer.priceFrom}
                    </span>
                    {printer.priceCurrency && (
                      <span className="text-sm text-white/30 ml-1.5">
                        {printer.priceCurrency}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2.5">
                    <a
                      href={printer.storeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[#F76B1C] hover:bg-[#E05A0F] text-white text-sm font-semibold rounded-lg transition-colors duration-200 hover:shadow-lg hover:shadow-[#F76B1C]/20"
                    >
                      View
                      <ExternalLink size={14} />
                    </a>
                    <a
                      href={printer.storeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-4 py-2.5 border border-white/[0.08] hover:border-white/[0.15] text-white/60 hover:text-white text-sm font-medium rounded-lg transition-all duration-200 hover:bg-white/[0.03]"
                    >
                      Request Sample
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ===== COMPARISON TOOL ===== */}
        <div className="mt-20 lg:mt-24">
          <div className="text-center mb-10">
            <h3 className="text-2xl lg:text-3xl font-bold text-white mb-3">
              Compare Printers Side by Side
            </h3>
            <p className="text-white/40 text-sm">
              Select any two printers to see how they stack up
            </p>
          </div>

          {/* Printer selectors */}
          <div className="flex items-center justify-center gap-4 sm:gap-6 mb-10">
            {/* Printer A dropdown */}
            <select
              value={compareA}
              onChange={(e) => {
                if (e.target.value !== compareB) setCompareA(e.target.value);
              }}
              className="px-5 py-3 rounded-xl bg-[#111111] border border-[#F76B1C]/30 text-white text-sm font-semibold focus:outline-none focus:border-[#F76B1C] focus:ring-1 focus:ring-[#F76B1C]/20 cursor-pointer appearance-none min-w-[160px] text-center"
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23F76B1C' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center', paddingRight: '36px' }}
            >
              {printersData.map((p) => (
                <option
                  key={`a-${p.id}`}
                  value={p.id}
                  disabled={p.id === compareB}
                  className="bg-[#111111] text-white"
                >
                  {p.name}
                </option>
              ))}
            </select>

            <span className="text-white/25 text-base font-bold">vs</span>

            {/* Printer B dropdown */}
            <select
              value={compareB}
              onChange={(e) => {
                if (e.target.value !== compareA) setCompareB(e.target.value);
              }}
              className="px-5 py-3 rounded-xl bg-[#111111] border border-[#F76B1C]/30 text-white text-sm font-semibold focus:outline-none focus:border-[#F76B1C] focus:ring-1 focus:ring-[#F76B1C]/20 cursor-pointer appearance-none min-w-[160px] text-center"
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23F76B1C' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center', paddingRight: '36px' }}
            >
              {printersData.map((p) => (
                <option
                  key={`b-${p.id}`}
                  value={p.id}
                  disabled={p.id === compareA}
                  className="bg-[#111111] text-white"
                >
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          {/* Comparison table */}
          {printerA && printerB && (
            <div className="rounded-2xl border border-white/[0.06] overflow-hidden bg-[#111111]">
              {/* Header row: images + names */}
              <div className="grid grid-cols-[1fr_1fr] divide-x divide-white/[0.06]">
                {[printerA, printerB].map((p) => (
                  <div
                    key={p.id}
                    className="p-6 lg:p-8 text-center bg-white/[0.01]"
                  >
                    <div className="relative flex items-center justify-center mb-4">
                      <div className="absolute w-[120px] h-[120px] bg-[#F76B1C]/[0.04] rounded-full blur-[50px]" />
                      <img loading="lazy"
                        src={p.image}
                        alt={p.name}
                        className="relative z-10 h-[120px] lg:h-[150px] w-auto object-contain"
                      />
                    </div>
                    <h4 className="text-xl lg:text-2xl font-bold text-white mb-1">
                      {p.name}
                    </h4>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${
                        badgeStyles[p.badgeStyle]
                      }`}
                    >
                      {p.badge}
                    </span>
                  </div>
                ))}
              </div>

              {/* Spec comparison rows */}
              {comparisonSpecs.map((spec, i) => {
                const valA = printerA[spec.key];
                const valB = printerB[spec.key];
                const isDifferent = valA !== valB;
                return (
                  <div
                    key={spec.key}
                    className={`grid grid-cols-[1fr_1fr] divide-x divide-white/[0.06] border-t border-white/[0.06] ${
                      i % 2 === 0 ? "bg-white/[0.01]" : "bg-transparent"
                    }`}
                  >
                    {[valA, valB].map((val, j) => (
                      <div
                        key={j}
                        className="px-6 lg:px-8 py-4 flex items-center gap-3"
                      >
                        <spec.icon
                          size={16}
                          className={
                            isDifferent
                              ? "text-[#F76B1C] shrink-0"
                              : "text-white/20 shrink-0"
                          }
                        />
                        <div>
                          <span className="text-[10px] text-white/30 font-medium uppercase tracking-wider block mb-0.5">
                            {spec.label}
                          </span>
                          <span
                            className={`text-sm font-semibold ${
                              isDifferent ? "text-[#F76B1C]" : "text-white/60"
                            }`}
                          >
                            {val}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}

              {/* Ideal for comparison */}
              <div className="grid grid-cols-[1fr_1fr] divide-x divide-white/[0.06] border-t border-white/[0.06]">
                {[printerA, printerB].map((p) => (
                  <div key={p.id} className="px-6 lg:px-8 py-5">
                    <span className="text-[10px] text-white/30 font-semibold uppercase tracking-wider block mb-3">
                      Ideal For
                    </span>
                    <div className="space-y-1.5">
                      {p.idealFor.map((item, j) => (
                        <div
                          key={j}
                          className="flex items-start gap-2 text-sm text-white/50"
                        >
                          <Check
                            size={13}
                            className="text-[#F76B1C]/60 mt-0.5 shrink-0"
                          />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Price + CTA comparison */}
              <div className="grid grid-cols-[1fr_1fr] divide-x divide-white/[0.06] border-t border-white/[0.06] bg-white/[0.02]">
                {[printerA, printerB].map((p) => (
                  <div
                    key={p.id}
                    className="px-6 lg:px-8 py-5 flex items-center justify-between"
                  >
                    <div>
                      <span className="text-xl font-bold text-white">
                        {p.priceFrom}
                      </span>
                      {p.priceCurrency && (
                        <span className="text-sm text-white/30 ml-1">
                          {p.priceCurrency}
                        </span>
                      )}
                    </div>
                    <a
                      href={p.storeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#F76B1C] hover:bg-[#E05A0F] text-white text-sm font-semibold rounded-lg transition-colors duration-200"
                    >
                      View
                      <ExternalLink size={14} />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bottom note */}
          <div className="mt-8 text-center">
            <p className="text-white/25 text-xs">
              All Form 4 series share the same MSLA engine, PreForm software,
              and Dashboard fleet management. Specs that differ are highlighted
              in orange.
            </p>
            <a
              href="#packages"
              onClick={(e) => {
                e.preventDefault();
                const el = document.querySelector("#packages");
                if (el)
                  window.scrollTo({
                    top:
                      el.getBoundingClientRect().top + window.scrollY - 80,
                    behavior: "smooth",
                  });
              }}
              className="inline-flex items-center gap-2 mt-4 text-[#F76B1C] hover:text-[#FF8C42] font-semibold text-sm transition-colors duration-200 group"
            >
              Compare Packages
              <ArrowRight
                size={16}
                className="group-hover:translate-x-1 transition-transform duration-200"
              />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
